import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";

const MAX_TILT = 12; // degrees
const RESET_DURATION = 350; // ms

const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

const getShadowColor = () => {
  if (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  ) {
    return "rgba(255,255,255,0.18)"; // light glow for dark mode
  }
  return "rgba(20,30,60,0.18)"; // dark shadow for light mode
};

const getShadowResetColor = () => {
  if (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  ) {
    return "rgba(255,255,255,0.12)";
  }
  return "rgba(20,30,60,0.12)";
};

const Clock: React.FC = () => {
  const [time, setTime] = useState(new Date());
  const [canTilt, setCanTilt] = useState(false);
  const circleRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const resetAnimRef = useRef<number | null>(null);
  const [shadowColor, setShadowColor] = useState(getShadowColor());
  const [shadowResetColor, setShadowResetColor] = useState(
    getShadowResetColor()
  );

  useEffect(() => {
    setCanTilt(!isTouchDevice());
  }, []);

  // Listen for dark mode changes
  useEffect(() => {
    const updateShadow = () => {
      setShadowColor(getShadowColor());
      setShadowResetColor(getShadowResetColor());
    };
    updateShadow();
    const observer = new MutationObserver(updateShadow);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Smoothly reset tilt to zero
  const smoothReset = () => {
    if (resetAnimRef.current) {
      cancelAnimationFrame(resetAnimRef.current);
    }
    const start = { ...tilt };
    const duration = RESET_DURATION;
    const startTime = performance.now();
    function animate(now: number) {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      setTilt({
        x: lerp(start.x, 0, t),
        y: lerp(start.y, 0, t),
      });
      if (t < 1) {
        resetAnimRef.current = requestAnimationFrame(animate);
      } else {
        setTilt({ x: 0, y: 0 });
        resetAnimRef.current = null;
      }
    }
    resetAnimRef.current = requestAnimationFrame(animate);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!circleRef.current) return;
    if (resetAnimRef.current) {
      cancelAnimationFrame(resetAnimRef.current);
      resetAnimRef.current = null;
    }
    const rect = circleRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    // Calculate offset from center, normalized to [-1, 1]
    const offsetX = (x - centerX) / (rect.width / 2);
    const offsetY = (y - centerY) / (rect.height / 2);
    // Invert Y for natural tilt
    const xTilt = -offsetY * MAX_TILT;
    const yTilt = offsetX * MAX_TILT;
    setTilt({ x: xTilt, y: yTilt });
  };

  const handleMouseLeave = () => {
    smoothReset();
  };

  const hourDeg = ((time.getHours() % 12) / 12) * 360 + time.getMinutes() * 0.5;
  const minuteDeg = time.getMinutes() * 6 + time.getSeconds() * 0.1;
  const secondDeg = time.getSeconds() * 6;

  // Shadow shifts opposite to tilt for realism
  const shadowX = tilt.y * 2;
  const shadowY = tilt.x * 2;

  return (
    <div className="flex justify-center items-center">
      <div
        className="relative bg-[#FDEAD7] dark:bg-gray-800 w-[380px] h-[380px] rounded-2xl flex justify-center items-center shadow-sm transition-all duration-300"
        style={{ perspective: "600px" }}
      >
        <motion.div
          ref={circleRef}
          style={{
            transformStyle: "preserve-3d",
            willChange: "transform, box-shadow",
            transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
            boxShadow: `${shadowX}px ${shadowY + 8}px 32px 0 ${
              shadowX === 0 && shadowY === 0 ? shadowResetColor : shadowColor
            }`,
          }}
          onMouseMove={canTilt ? handleMouseMove : undefined}
          onMouseLeave={canTilt ? handleMouseLeave : undefined}
          className="w-[200px] h-[200px] bg-domichron-orange rounded-full relative"
        >
          <div
            className="absolute w-[6px] h-[60px] bg-white rounded-sm origin-bottom left-1/2 top-1/2"
            style={{
              transform: `translate(-50%, -100%) rotate(${hourDeg}deg)`,
            }}
          />
          <div
            className="absolute w-[4px] h-[90px] bg-white rounded-sm origin-bottom left-1/2 top-1/2"
            style={{
              transform: `translate(-50%, -100%) rotate(${minuteDeg}deg)`,
            }}
          />
          <div
            className="absolute w-[2px] h-[90px] bg-white origin-bottom left-1/2 top-1/2"
            style={{
              transform: `translate(-50%, -100%) rotate(${secondDeg}deg)`,
            }}
          />
          <div className="absolute w-[16px] h-[16px] bg-white rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10" />
        </motion.div>
      </div>
    </div>
  );
};

export default Clock;
